import { CtaButton } from "./CtaButton";
import { CmsPageShell } from "./CmsPageShell";
import { resolveCta } from "./ctaDefaults";
import {
  LEAD_FORM_ANCHOR_ID,
  type CmsPageRenderProps,
  type LongFormArticleRenderProps,
  type LongFormContentBlockRenderProps,
} from "./renderProps";
import { advisorHomePath, newsArticlePath, newsIndexPath } from "./routes";
import { renderTextWithLineBreaks } from "./text";

export function NewsIndexPage(props: CmsPageRenderProps) {
  const articles = newsArticles(props.longFormContent);
  return (
    <CmsPageShell page={props} mainClassName="cms-longform-page">
      <section className="cms-section cms-longform-index">
        <div className="cms-wrap">
          <a className="cms-longform-back" href={advisorHomePath(props.slug)}>
            {props.siteName}
          </a>
          <div className="cms-eyebrow-row">
            <span className="cms-eyebrow">News</span>
          </div>
          <h1 className="cms-longform-title">
            News from {props.advisorDisplayName}
          </h1>
          <div className="cms-longform-list">
            {articles.map((article) => (
              <article className="cms-longform-card" key={article.articleId}>
                <div className="cms-longform-card__meta">
                  {articleMeta(article)}
                </div>
                <h2>
                  <a href={newsArticlePath(props.slug, article.slug)}>
                    {article.title}
                  </a>
                </h2>
                {article.excerpt ? (
                  <p>{renderTextWithLineBreaks(article.excerpt)}</p>
                ) : null}
              </article>
            ))}
          </div>
        </div>
      </section>
    </CmsPageShell>
  );
}

export function LongFormArticlePage({
  page,
  article,
}: {
  page: CmsPageRenderProps;
  article: LongFormArticleRenderProps;
}) {
  const defaultCtaUrl = `${advisorHomePath(page.slug)}#${LEAD_FORM_ANCHOR_ID}`;

  return (
    <CmsPageShell page={page} mainClassName="cms-longform-page">
      <article>
        <header className="cms-section cms-longform-hero">
          <div className="cms-wrap cms-longform-hero__grid">
            <div>
              <a className="cms-longform-back" href={newsIndexPath(page.slug)}>
                News
              </a>
              <div className="cms-longform-card__meta">
                {articleMeta(article)}
              </div>
              <h1 className="cms-longform-title">{article.title}</h1>
              {article.excerpt ? (
                <p className="cms-longform-dek">
                  {renderTextWithLineBreaks(article.excerpt)}
                </p>
              ) : null}
            </div>
            {article.heroImage ? (
              <figure className="cms-longform-hero__image">
                <img src={article.heroImage.src} alt={article.heroImage.alt} />
                {article.heroImage.caption ? (
                  <figcaption>{article.heroImage.caption}</figcaption>
                ) : null}
              </figure>
            ) : null}
          </div>
        </header>
        <section className="cms-section cms-longform-body">
          <div className="cms-wrap cms-longform-prose">
            {article.body.map((block, index) =>
              renderBlock(block, index, defaultCtaUrl),
            )}
          </div>
        </section>
      </article>
    </CmsPageShell>
  );
}

export function newsArticles(
  articles: LongFormArticleRenderProps[],
): LongFormArticleRenderProps[] {
  return articles.filter((article) => article.contentType === "NEWS_ARTICLE");
}

function renderBlock(
  block: LongFormContentBlockRenderProps,
  index: number,
  defaultCtaUrl: string,
) {
  switch (block.blockType) {
    case "HEADING": {
      const Heading = block.level === 3 ? "h3" : "h2";
      return <Heading key={index}>{block.text}</Heading>;
    }
    case "PARAGRAPH":
      return <p key={index}>{renderTextWithLineBreaks(block.text ?? "")}</p>;
    case "QUOTE":
      return (
        <blockquote key={index}>
          <p>{renderTextWithLineBreaks(block.text ?? "")}</p>
          {block.attribution ? <cite>{block.attribution}</cite> : null}
        </blockquote>
      );
    case "IMAGE":
      return (
        <figure key={index}>
          {block.image ? (
            <img src={block.image.src} alt={block.image.alt} />
          ) : (
            <div
              className="cms-longform-image-placeholder"
              aria-label="Image pending"
            />
          )}
          {(block.caption ?? block.image?.caption) ? (
            <figcaption>{block.caption ?? block.image?.caption}</figcaption>
          ) : null}
        </figure>
      );
    case "CTA":
      const cta = resolveCta(block.label, block.url, defaultCtaUrl);
      return (
        <p className="cms-longform-cta" key={index}>
          <CtaButton label={cta.label} url={cta.url} />
        </p>
      );
    default:
      return null;
  }
}

const DISPLAY_DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
  month: "long",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

function articleMeta(article: LongFormArticleRenderProps): string {
  return [formatDate(article.displayDate), article.authorDisplayName]
    .filter(Boolean)
    .join(" - ");
}

function formatDate(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return DISPLAY_DATE_FORMAT.format(date);
}
